import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { getUnits, createUnit, updateUnit, deleteUnit } from '../../services/productService';
import { Unit } from '../../types/product';

const UnitList: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // For unit form dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [currentUnit, setCurrentUnit] = useState<Unit | null>(null);
  const [unitName, setUnitName] = useState('');
  const [unitValue, setUnitValue] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // For delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const data = await getUnits(page + 1, rowsPerPage, search);
      setUnits(data.units);
      setTotalUnits(data.pagination.total);
      setTotalPages(data.pagination.pages);
      setError(null);
    } catch (err) {
      setError('حدث خطأ أثناء جلب الوحدات');
      console.error('Error details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [page, rowsPerPage, search]);

  const handleOpenAddDialog = () => {
    setFormMode('add');
    setCurrentUnit(null);
    setUnitName('');
    setUnitValue('');
    setFormError(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (unit: Unit) => {
    setFormMode('edit');
    setCurrentUnit(unit);
    setUnitName(unit.name);
    setUnitValue(unit.value);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    // Validate form
    if (!unitName.trim()) {
      setFormError('اسم الوحدة مطلوب');
      return;
    }
    if (!unitValue.trim()) {
      setFormError('قيمة الوحدة مطلوبة');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (formMode === 'add') {
        await createUnit({ name: unitName, value: unitValue });
      } else if (formMode === 'edit' && currentUnit?._id) {
        await updateUnit(currentUnit._id, { name: unitName, value: unitValue });
      }
      handleCloseDialog();
      fetchUnits();
    } catch (err) {
      setFormError('حدث خطأ أثناء حفظ الوحدة');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (unit: Unit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUnitToDelete(null);
  };

  const handleDeleteUnit = async () => {
    if (!unitToDelete || !unitToDelete._id) return;

    try {
      await deleteUnit(unitToDelete._id);
      handleCloseDeleteDialog();
      fetchUnits();
    } catch (err) {
      setError('حدث خطأ أثناء حذف الوحدة');
      console.error(err);
      handleCloseDeleteDialog();
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          قائمة الوحدات
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          إضافة وحدة جديدة
        </Button>
      </Box>

      <Box sx={{ display: 'flex', mb: 3 }}>
        <TextField
          label="بحث"
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="units table">
          <TableHead>
            <TableRow>
              <TableCell>اسم الوحدة</TableCell>
              <TableCell>القيمة</TableCell>
              <TableCell align="center">إجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : units.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  لا توجد وحدات متاحة
                </TableCell>
              </TableRow>
            ) : (
              units.map((unit) => (
                <TableRow key={unit._id}>
                  <TableCell>{unit.name}</TableCell>
                  <TableCell>{unit.value}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditDialog(unit)}
                      title="تعديل"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(unit)}
                      title="حذف"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalUnits}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="عدد العناصر في الصفحة:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
        }
      />

      {/* Add/Edit Unit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {formMode === 'add' ? 'إضافة وحدة جديدة' : 'تعديل الوحدة'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <DialogContentText color="error" sx={{ mb: 2 }}>
              {formError}
            </DialogContentText>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="اسم الوحدة"
            type="text"
            fullWidth
            variant="outlined"
            value={unitName}
            onChange={(e) => setUnitName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="القيمة"
            type="text"
            fullWidth
            variant="outlined"
            value={unitValue}
            onChange={(e) => setUnitValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            إلغاء
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف الوحدة "{unitToDelete?.name}"؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteUnit} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnitList;